import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BadgeCheck, Crown } from "lucide-react";
import { api, storeMember } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const styles = ["Reds", "Whites", "Bubbles", "A mixed case", "Surprise me"];

export const JoinSection = ({ member, setMember, giftIntent, setGiftIntent, onUpgrade, upgrading }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [wineStyle, setWineStyle] = useState("");
  const [joining, setJoining] = useState(false);

  const join = async (e) => {
    e.preventDefault();
    setJoining(true);
    try {
      const res = await api.post("/members/join", {
        name,
        email,
        wine_style: wineStyle || null,
        is_gift: giftIntent,
      });
      storeMember(res.data);
      setMember(res.data);
      toast.success("Welcome to the club — membership is free, forever.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went sideways. Try again.");
    } finally {
      setJoining(false);
    }
  };

  return (
    <section id="join" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36" data-testid="join-section">
      <div className="grid grid-cols-1 gap-16 md:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="font-serif text-4xl tracking-tight md:text-6xl">
            Your glass is <em className="text-wine">waiting.</em>
          </h2>
          <p className="mt-8 max-w-sm text-sm leading-relaxed text-ink/60 md:text-base">
            Thirty seconds, no card, no commitment. You're a member the moment you
            hit join — and you'll never pay a cent in club fees.
          </p>
          <p className="mt-6 text-xs uppercase tracking-[0.25em] text-ink/45">
            Free forever · Premium when you want it
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          {member ? (
            <div className="border border-ink/15 bg-[#F3EEE3] p-10 md:p-12" data-testid="member-panel">
              <div className="flex items-center gap-4">
                {member.tier === "premium" ? (
                  <Crown className="h-8 w-8 text-gold" />
                ) : (
                  <BadgeCheck className="h-8 w-8 text-wine" />
                )}
                <div>
                  <p className="font-serif text-2xl tracking-tight" data-testid="member-panel-name">
                    Welcome, {member.name.split(" ")[0]}
                  </p>
                  <p className="text-xs uppercase tracking-[0.25em] text-ink/50" data-testid="member-panel-tier">
                    {member.tier === "premium" ? "Premium member" : "Free member"} · since{" "}
                    {new Date(member.created_at).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                  </p>
                </div>
              </div>
              <p className="mt-6 text-sm leading-relaxed text-ink/65">
                {member.tier === "premium"
                  ? "Priority allocation, free shipping and the reserve cellar are all yours. First pick of every drop."
                  : "You're on the list for the next drop. Want priority bottles, free shipping and the reserve cellar? Premium is one click away."}
              </p>
              {member.tier !== "premium" && (
                <Button
                  data-testid="member-panel-upgrade-button"
                  onClick={onUpgrade}
                  disabled={upgrading}
                  className="mt-8 rounded-full bg-wine px-8 py-6 text-sm uppercase tracking-widest text-paper hover:bg-ink"
                >
                  {upgrading ? "Opening checkout…" : "Upgrade to Premium — $19/mo"}
                </Button>
              )}
            </div>
          ) : (
            <form onSubmit={join} className="border border-ink/15 bg-[#F3EEE3] p-10 md:p-12" data-testid="join-form">
              <div className="space-y-6">
                <div>
                  <label htmlFor="join-name" className="text-xs uppercase tracking-[0.25em] text-ink/50">Name</label>
                  <Input
                    id="join-name"
                    data-testid="join-name-input"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="mt-2 rounded-none border-ink/25 bg-paper px-4 py-6 focus-visible:ring-wine"
                  />
                </div>
                <div>
                  <label htmlFor="join-email" className="text-xs uppercase tracking-[0.25em] text-ink/50">Email</label>
                  <Input
                    id="join-email"
                    data-testid="join-email-input"
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-2 rounded-none border-ink/25 bg-paper px-4 py-6 focus-visible:ring-wine"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.25em] text-ink/50">What do you pour?</label>
                  <Select value={wineStyle} onValueChange={setWineStyle}>
                    <SelectTrigger data-testid="join-style-select" className="mt-2 rounded-none border-ink/25 bg-paper px-4 py-6 focus:ring-wine">
                      <SelectValue placeholder="Pick a style (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-paper">
                      {styles.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`join-style-${s.toLowerCase().replace(/\s/g, "-")}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex cursor-pointer items-center gap-3 text-sm text-ink/70">
                  <Checkbox
                    data-testid="join-gift-checkbox"
                    checked={giftIntent}
                    onCheckedChange={(v) => setGiftIntent(Boolean(v))}
                    className="border-ink/40 data-[state=checked]:bg-wine"
                  />
                  I'm joining to gift wine to someone
                </label>
                <Button
                  data-testid="join-submit-button"
                  type="submit"
                  disabled={joining}
                  className="w-full rounded-full bg-wine py-7 text-sm uppercase tracking-widest text-paper hover:bg-ink"
                >
                  {joining ? "Pouring you in…" : "Join the club — free"}
                </Button>
                <p className="text-center text-xs text-ink/45">No card required. No fees, ever.</p>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
